<script setup>
// Operator copilot panel («Ассистент») in the conversation view, running on
// our native assistant module (assistant copilot threads/messages API).
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import AssistantAgentsAPI from 'dashboard/api/assistant/agents';
import CopilotMessagesAPI from 'dashboard/api/assistant/copilotMessages';
import CopilotThreadsAPI from 'dashboard/api/assistant/copilotThreads';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const currentChat = useMapGetter('getSelectedChat');

// Р14: панель держит СОБСТВЕННЫЙ список ассистентов оператора и грузит его
// напрямую по API (kind=operator_assistant). Раньше список брался из общего
// стора assistantAgents, который перезаписывают другие вкладки меню (напр.
// «Агенты» грузит клиентских агентов) — из-за этого панель показывала то
// «нет ассистентов», то список. Изолированный список убирает рассинхрон.
const assistants = ref([]);
const fetchingAssistants = ref(false);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedCopilotThreadId = ref(null);
const threadMessages = ref([]);

// Copilot message components expect { message_type, message: { content } }.
const toPanelMessage = payloadMessage => ({
  id: payloadMessage.id,
  message_type: payloadMessage.message_type,
  message: { content: payloadMessage.content },
});

const messages = computed(() => threadMessages.value);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Р16 п.6: активного ассистента назначает администратор — оператор в чате
// больше НЕ выбирает. Бэкенд отдаёт назначенных с primary-первым, поэтому
// берём первого из списка.
const activeAssistant = computed(() => assistants.value[0] || null);

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const shouldShowCopilotPanel = computed(() => {
  const isAssistantEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.AI_ASSISTANT
  );
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  // Прячем панель только на ПЕРВОЙ загрузке (пока данных ещё нет); при
  // повторном открытии список уже есть — не мигаем во время фонового обновления.
  return (
    isAssistantEnabled &&
    isCopilotPanelOpen &&
    !(fetchingAssistants.value && !assistants.value.length)
  );
});

const handleReset = () => {
  selectedCopilotThreadId.value = null;
  threadMessages.value = [];
};

const appendPayloadMessages = payload => {
  (payload || []).forEach(message => {
    threadMessages.value.push(toPanelMessage(message));
  });
};

const sendMessage = async message => {
  // Р16 п.3а: показываем сообщение оператора СРАЗУ (оптимистичный эхо) — пока
  // модель думает, оно не «пропадает». Последнее сообщение = user → в Copilot
  // сразу виден индикатор «Ассистент думает» (CopilotLoader).
  const optimisticId = `local-${Date.now()}`;
  threadMessages.value.push({
    id: optimisticId,
    message_type: 'user',
    message: { content: message },
  });
  const dropOptimistic = () => {
    threadMessages.value = threadMessages.value.filter(
      m => m.id !== optimisticId
    );
  };

  try {
    if (selectedCopilotThreadId.value) {
      const { data } = await CopilotMessagesAPI.create({
        threadId: selectedCopilotThreadId.value,
        message,
      });
      dropOptimistic();
      appendPayloadMessages(data.payload);
    } else {
      const { data } = await CopilotThreadsAPI.create({
        assistant_id: activeAssistant.value?.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = data.id;
      dropOptimistic();
      appendPayloadMessages(data.messages);
    }
  } catch (error) {
    dropOptimistic();
    useAlert(error?.response?.data?.error || error.message);
  }
};

const fetchOperatorAssistants = async () => {
  // Р17 п.6: НЕ гейтим по фиче здесь. Видимость панели уже гейтит
  // shouldShowCopilotPanel, а на перезагрузке список фич аккаунта мог ещё не
  // догрузиться (accountId из роута готов раньше features) — прежний ранний
  // выход оставлял панель пустой, хотя ассистент есть. Если фича выключена —
  // API вернёт 403, и список просто останется пустым.
  fetchingAssistants.value = true;
  try {
    const { data } = await AssistantAgentsAPI.get({
      kind: 'operator_assistant',
      assignedToMe: true,
    });
    assistants.value = data?.payload || [];
  } catch {
    assistants.value = [];
  } finally {
    fetchingAssistants.value = false;
  }
};

// Р17 п.6: грузим список, КАК ТОЛЬКО известен аккаунт. На перезагрузке
// страницы onMounted мог сработать раньше, чем стор догрузил аккаунт
// (currentAccountId=undefined → ранний выход по фиче-гейту → панель пустая,
// хотя ассистент есть). Watch с immediate ловит и готовый, и поздний аккаунт.
watch(
  currentAccountId,
  accountId => {
    if (accountId) fetchOperatorAssistants();
  },
  { immediate: true }
);

// Обновляем список при каждом открытии панели, чтобы свежесозданные
// ассистенты появлялись без перезагрузки страницы.
watch(
  () => uiSettings.value.is_copilot_panel_open,
  isOpen => {
    if (isOpen) fetchOperatorAssistants();
  }
);
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
    />
  </div>
  <template v-else />
</template>
